import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import publicApi from '../services/publicApi';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

type Option = { id: string; label: string };
type UserOption = { id: string; username: string };

type DashboardResponse = {
  kpis: { bytesIn: string; bytesOut: string; bytesTotal: string };
  quota: {
    usedBytesTotal: string;
    limitBytesTotal: string | null;
    availableBytesTotal: string | null;
    percentUsed: number | null;
    updatedAt: string | null;
  };
  series: { bucketStart: string; bytesIn: string; bytesOut: string; bytesTotal: string }[];
  lastUpdated: string | null;
};

const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / Math.pow(1024, idx);
  const digits = scaled >= 10 || idx === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${units[idx]}`;
};

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const SearchableSelect: React.FC<{
  label: string;
  placeholder?: string;
  options: Option[];
  value: Option | null;
  onChange: (option: Option | null) => void;
  onSearch?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
}> = ({ label, placeholder, options, value, onChange, onSearch, disabled, loading }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery(value?.label || '');
    }
  }, [value, open]);

  const filtered = useMemo(() => {
    if (onSearch) return options;
    const needle = query.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(needle));
  }, [options, query, onSearch]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={open ? query : value?.label || ''}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => {
          setOpen(true);
          setQuery(value?.label || '');
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          onSearch?.(next);
        }}
        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
      />
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-lg border bg-white shadow">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Nenhum resultado</div>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Medidor tipo velocímetro: verde (baixo uso) até vermelho (limite quase batido).
const UsageGauge: React.FC<{ percent: number; size?: number }> = ({ percent, size = 220 }) => {
  const clamped = Math.max(0, Math.min(100, percent));
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 22;
  const strokeWidth = 20;

  const toPoint = (p: number, r: number) => {
    const angleDeg = 180 - (p / 100) * 180;
    const angleRad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(angleRad), y: cy - r * Math.sin(angleRad) };
  };

  const bands: { from: number; to: number; color: string }[] = [
    { from: 0, to: 20, color: '#16a34a' },
    { from: 20, to: 40, color: '#84cc16' },
    { from: 40, to: 60, color: '#eab308' },
    { from: 60, to: 80, color: '#f97316' },
    { from: 80, to: 100, color: '#dc2626' }
  ];

  const needleTip = toPoint(clamped, radius - strokeWidth / 2 - 4);
  const color =
    clamped >= 80 ? '#dc2626' : clamped >= 60 ? '#f97316' : clamped >= 40 ? '#eab308' : clamped >= 20 ? '#84cc16' : '#16a34a';

  return (
    <svg width={size} height={size / 2 + 40} viewBox={`0 0 ${size} ${size / 2 + 40}`}>
      {bands.map((band) => {
        const start = toPoint(band.from, radius);
        const end = toPoint(band.to, radius);
        return (
          <path
            key={band.from}
            d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`}
            stroke={band.color}
            strokeWidth={strokeWidth}
            fill="none"
          />
        );
      })}
      <line
        x1={cx}
        y1={cy}
        x2={needleTip.x}
        y2={needleTip.y}
        stroke="#1f2937"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r={7} fill="#1f2937" />
      <text
        x={cx}
        y={cy + 30}
        textAnchor="middle"
        style={{ fontSize: 26, fontWeight: 700, fill: color }}
      >
        {clamped.toFixed(0)}%
      </text>
      <text x={cx} y={cy + 48} textAnchor="middle" style={{ fontSize: 11, fill: '#6b7280' }}>
        usado do limite
      </text>
    </svg>
  );
};

const PublicConsumo: React.FC = () => {
  const [companies, setCompanies] = useState<Option[]>([]);
  const [vessels, setVessels] = useState<Option[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);

  const [company, setCompany] = useState<Option | null>(null);
  const [vessel, setVessel] = useState<Option | null>(null);
  const [user, setUser] = useState<UserOption | null>(null);

  const [userSearch, setUserSearch] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingVessels, setLoadingVessels] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [accessCode, setAccessCode] = useState('');
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateError, setValidateError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [viewToken, setViewToken] = useState('');

  const [groupBy, setGroupBy] = useState<'hour' | 'day' | 'month'>('day');
  const [period, setPeriod] = useState<'24h' | '7d' | '30d' | '12m'>('7d');
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState('');

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const res = await publicApi.get('/public/consumo/companies');
      setCompanies((res.data || []).map((item: any) => ({ id: item.id, label: item.name })));
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const fetchVessels = useCallback(async (companyId: string) => {
    setLoadingVessels(true);
    try {
      const res = await publicApi.get('/public/consumo/vessels', { params: { companyId } });
      setVessels((res.data || []).map((item: any) => ({ id: item.id, label: item.name })));
    } finally {
      setLoadingVessels(false);
    }
  }, []);

  const fetchUsers = useCallback(async (vesselId: string, query: string) => {
    setLoadingUsers(true);
    try {
      const res = await publicApi.get('/public/consumo/users', {
        params: { vesselId, q: query, limit: 50 }
      });
      setUsers((res.data?.items || []).map((item: any) => ({ id: item.id, username: item.username })));
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (!company) return;
    setVessels([]);
    setVessel(null);
    setUsers([]);
    setUser(null);
    fetchVessels(company.id);
  }, [company, fetchVessels]);

  useEffect(() => {
    if (!vessel) return;
    setUsers([]);
    setUser(null);
    const handle = setTimeout(() => {
      fetchUsers(vessel.id, userSearch);
    }, 300);
    return () => clearTimeout(handle);
  }, [vessel, userSearch, fetchUsers]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const handle = setInterval(() => {
      setCooldownSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(handle);
  }, [cooldownSeconds]);

  const handleValidate = async () => {
    setValidateError('');
    setDashboardError('');
    if (!company || !vessel || !user || accessCode.trim().length === 0) {
      setValidateError('Preencha todos os campos.');
      return;
    }
    setValidateLoading(true);
    try {
      const res = await publicApi.post('/public/consumo/validate', {
        companyId: company.id,
        vesselId: vessel.id,
        username: user.username,
        accessCode: accessCode.trim()
      });
      setViewToken(res.data?.viewToken || '');
      setValidateError('');
    } catch (error: any) {
      const message =
        error?.response?.data?.error === 'Excesso de tentativas, aguarde alguns segundos'
          ? `Excesso de tentativas, aguarde ${error?.response?.data?.retryAfter || 60}s`
          : 'Código inválido';
      setValidateError(message);
      if (error?.response?.data?.retryAfter) {
        setCooldownSeconds(Number(error.response.data.retryAfter));
      }
    } finally {
      setValidateLoading(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    if (!viewToken || !vessel || !user) return;
    setLoadingDashboard(true);
    setDashboardError('');
    try {
      const res = await publicApi.get('/public/consumo/dashboard', {
        params: {
          vesselId: vessel.id,
          username: user.username,
          groupBy,
          period
        },
        headers: { Authorization: `Bearer ${viewToken}` }
      });
      setDashboard(res.data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setViewToken('');
        setDashboard(null);
        setValidateError('Sessão expirada. Por favor, valide novamente.');
      } else {
        setDashboardError('Falha ao carregar consumo');
      }
    } finally {
      setLoadingDashboard(false);
    }
  }, [viewToken, vessel, user, groupBy, period]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!viewToken) return;
    const interval = setInterval(() => {
      fetchDashboard();
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [viewToken, fetchDashboard]);

  const seriesData = useMemo(() => {
    return (dashboard?.series || []).map((row) => ({
      ...row,
      bucketLabel: format(
        new Date(row.bucketStart),
        groupBy === 'hour' ? 'HH:mm' : groupBy === 'day' ? 'dd/MM' : 'MMM/yy'
      ),
      bytesInNum: toNumber(row.bytesIn),
      bytesOutNum: toNumber(row.bytesOut),
      bytesTotalNum: toNumber(row.bytesTotal)
    }));
  }, [dashboard, groupBy]);

  const kpis = useMemo(() => {
    const bytesIn = toNumber(dashboard?.kpis?.bytesIn);
    const bytesOut = toNumber(dashboard?.kpis?.bytesOut);
    const bytesTotal = toNumber(dashboard?.kpis?.bytesTotal);
    return {
      bytesIn: formatBytes(bytesIn),
      bytesOut: formatBytes(bytesOut),
      bytesTotal: formatBytes(bytesTotal)
    };
  }, [dashboard]);

  const quota = useMemo(() => {
    const q = dashboard?.quota;
    const hasLimit = !!q?.limitBytesTotal;
    const percent = q?.percentUsed ?? 0;
    return {
      hasLimit,
      percent,
      used: formatBytes(toNumber(q?.usedBytesTotal)),
      limit: hasLimit ? formatBytes(toNumber(q?.limitBytesTotal)) : null,
      available: hasLimit ? formatBytes(toNumber(q?.availableBytesTotal)) : null,
      updatedAt: q?.updatedAt || null
    };
  }, [dashboard]);

  const resetToSelection = () => {
    setViewToken('');
    setDashboard(null);
    setAccessCode('');
    setValidateError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold">Consulta de Consumo</h1>
          <p className="text-gray-500 mt-2">Acesse seu consumo por hora, dia ou mês.</p>
        </div>

        {!viewToken ? (
          <div className="bg-white rounded-xl shadow p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Passo 1 — Seleção de contexto</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <SearchableSelect
                  label="Empresa"
                  placeholder="Selecione a empresa"
                  options={companies}
                  value={company}
                  onChange={(opt) => setCompany(opt)}
                  loading={loadingCompanies}
                />
                <SearchableSelect
                  label="Vessel"
                  placeholder="Selecione o vessel"
                  options={vessels}
                  value={vessel}
                  onChange={(opt) => setVessel(opt)}
                  loading={loadingVessels}
                  disabled={!company}
                />
                <SearchableSelect
                  label="Usuário Hotspot"
                  placeholder="Buscar usuário"
                  options={users.map((item) => ({ id: item.id, label: item.username }))}
                  value={user ? { id: user.id, label: user.username } : null}
                  onChange={(opt) =>
                    setUser(opt ? { id: opt.id, username: opt.label } : null)
                  }
                  onSearch={setUserSearch}
                  loading={loadingUsers}
                  disabled={!vessel}
                />
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold">Passo 2 — Código de acesso</h2>
              <p className="text-sm text-gray-500 mt-1">
                O código deve ser exatamente igual ao username do usuário selecionado.
              </p>
              <div className="mt-3 flex flex-col md:flex-row gap-3 md:items-center">
                <input
                  type="text"
                  placeholder="Código de acesso"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={validateLoading || cooldownSeconds > 0}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {validateLoading ? 'Validando...' : 'Validar'}
                </button>
              </div>
              {validateError && (
                <p className="mt-2 text-sm text-red-600">{validateError}</p>
              )}
              {cooldownSeconds > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Aguarde {cooldownSeconds}s para tentar novamente.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">{user?.username}</h2>
                <p className="text-sm text-gray-500">
                  {company?.label} · {vessel?.label}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={fetchDashboard}
                  disabled={loadingDashboard}
                  className="border px-4 py-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  {loadingDashboard ? 'Atualizando...' : 'Atualizar'}
                </button>
                <button
                  type="button"
                  onClick={resetToSelection}
                  className="border px-4 py-2 rounded-lg hover:bg-gray-100"
                >
                  Voltar
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Sua cota</h3>
              {quota.hasLimit ? (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <UsageGauge percent={quota.percent} />
                  <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-500">Consumido</p>
                      <p className="text-xl font-semibold mt-1">{quota.used}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                      <p className="text-sm text-green-700">Disponível</p>
                      <p className="text-xl font-semibold mt-1 text-green-700">{quota.available}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-500">Limite total</p>
                      <p className="text-xl font-semibold mt-1">{quota.limit}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Este usuário não tem limite de dados configurado.</p>
              )}
              {quota.updatedAt && (
                <p className="text-xs text-gray-400 mt-4">
                  Atualizado em {format(new Date(quota.updatedAt), 'dd/MM/yyyy HH:mm:ss')}
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {(['24h', '7d', '30d', '12m'] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPeriod(item)}
                      className={`px-3 py-1.5 rounded-full border text-sm ${
                        period === item ? 'bg-blue-600 text-white border-blue-600' : 'bg-white'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  {(['hour', 'day', 'month'] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setGroupBy(item)}
                      className={`px-4 py-2 rounded-lg text-sm border ${
                        groupBy === item ? 'bg-gray-900 text-white border-gray-900' : 'bg-white'
                      }`}
                    >
                      {item === 'hour' ? 'Hora' : item === 'day' ? 'Dia' : 'Mês'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow p-5">
                <p className="text-sm text-gray-500">Download no período</p>
                <p className="text-2xl font-semibold mt-2">{kpis.bytesIn}</p>
              </div>
              <div className="bg-white rounded-xl shadow p-5">
                <p className="text-sm text-gray-500">Upload no período</p>
                <p className="text-2xl font-semibold mt-2">{kpis.bytesOut}</p>
              </div>
              <div className="bg-white rounded-xl shadow p-5">
                <p className="text-sm text-gray-500">Total no período</p>
                <p className="text-2xl font-semibold mt-2">{kpis.bytesTotal}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Consumo</h3>
                {dashboard?.lastUpdated && (
                  <p className="text-xs text-gray-500">
                    Última atualização: {format(new Date(dashboard.lastUpdated), 'yyyy-MM-dd HH:mm:ss')}
                  </p>
                )}
              </div>

              {loadingDashboard ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : dashboardError ? (
                <p className="text-sm text-red-600">{dashboardError}</p>
              ) : seriesData.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Ainda não há dados de consumo para este usuário neste período.
                </p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={seriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucketLabel" />
                      <YAxis tickFormatter={(value) => formatBytes(Number(value))} />
                      <Tooltip
                        formatter={(value: any) => formatBytes(Number(value))}
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.bucketStart
                            ? format(new Date(payload[0].payload.bucketStart), 'yyyy-MM-dd HH:mm')
                            : ''
                        }
                      />
                      <Bar dataKey="bytesInNum" name="Download" fill="#2563eb" />
                      <Bar dataKey="bytesOutNum" name="Upload" fill="#16a34a" />
                      <Bar dataKey="bytesTotalNum" name="Total" fill="#0f172a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {seriesData.length > 0 && (
                <div className="mt-6 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-4">Período</th>
                        <th className="py-2 pr-4">Download</th>
                        <th className="py-2 pr-4">Upload</th>
                        <th className="py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seriesData.map((row) => (
                        <tr key={row.bucketStart} className="border-b last:border-b-0">
                          <td className="py-2 pr-4">{row.bucketLabel}</td>
                          <td className="py-2 pr-4">{formatBytes(row.bytesInNum)}</td>
                          <td className="py-2 pr-4">{formatBytes(row.bytesOutNum)}</td>
                          <td className="py-2">{formatBytes(row.bytesTotalNum)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicConsumo;
